/**
 * WorkspaceHeader Component
 * Notion-style top bar with search, nav, peek toggles, and user menu.
 */
import React, { useState } from 'react';
import { Box, Group, Text, TextInput, ActionIcon, Avatar, Menu, Divider, Tooltip } from '@mantine/core';
import { IconSearch, IconQuestionMark, IconMenu2, IconChevronDown, IconSun, IconMoon, IconSparkles, IconPlus, IconUser, IconSettings, IconLogout, IconPalette } from '@tabler/icons-react';
import { useMantineColorScheme } from '@mantine/core';
import { usePeek } from '../common/PeekManager';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { loadDatabases } from '../../../core/workspace/databaseStorage';
import { useTheme } from '../../themes';
import { themes as allThemes, type ThemeId } from '../../themes/theme-config';
import { useDockPin, getPinnedChipId } from '../cockpit/Dock/useDockPin';
import { Profile } from '../profile/Profile';
import { freshcardsBuildHash, freshcardsVersion } from '../../../version/provider';
// F 490 — the notification centre (bell + unread badge + inbox). The bell
// icon itself is no longer imported here: the centre renders it, so the
// icon and the badge cannot drift apart.
import { NotificationCentre } from './NotificationCentre';
import { useOptionalReminderContext } from './ReminderProvider';
import { ReminderStore } from '../../../core/reminder/store';
import type { Notification } from '../../../types/models/Reminder';

export const WorkspaceHeader: React.FC = () => {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const { isPeekOpen, togglePeek } = usePeek();
  const { toggleSidebar, goToHome, pages, selectPage, currentPage } = useWorkspace();
  const { theme, themeId, setTheme, modeName, setMode } = useTheme();
  // F 490 — the reminder context is OPTIONAL here. The header is mounted in
  // places that do not have a provider (the storybook-ish surfaces, some
  // tests), and a header that THROWS without a provider would take the
  // whole app down. So the context is read optionally, and the bell falls
  // back to its own store when there is no provider.
  const reminderCtx = useOptionalReminderContext();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  // F 345 — the Profile Face. Opens from the avatar Menu in the top bar so
  // the user can reach identity + engine + settings without leaving the page.
  const [profileOpen, setProfileOpen] = useState(false);

  // V5.1+Cockpit: Dock pin — auto-restore last-toggled peek on mount
  const quickNotePin = useDockPin('quick-note');
  const aiChatPin = useDockPin('ai-chat');
  React.useEffect(() => {
    const pinned = getPinnedChipId();
    if (pinned === 'quick-note' && !isPeekOpen('quick-note')) togglePeek('quick-note');
    if (pinned === 'ai-chat' && !isPeekOpen('ai-chat')) togglePeek('ai-chat');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const handleTogglePeek = (id: 'quick-note' | 'ai-chat') => {
    togglePeek(id);
    (id === 'quick-note' ? quickNotePin : aiChatPin).pin();
  };

  /**
   * F 490 — the store the bell reads through.
   *
   * When there IS a provider, its store is used, so the bell and the toast
   * share one poll. When there is NOT (the header rendered outside the
   * provider), a local store is used so the bell still works instead of
   * disappearing.
   */
  const fallbackStore = React.useMemo(() => new ReminderStore(), []);
  const reminderStore = reminderCtx?.store ?? fallbackStore;

  /**
   * F 490 — clicking a notification navigates to the node that owns the
   * date. The notification carries `parent_node_id`, which is the node the
   * reminder hung off. If that node is a page, the workspace's page
   * selection is the right navigation; if it is a task, the header cannot
   * open a task pane on its own, so the notification is simply marked read
   * — which the centre has already done before this runs.
   */
  const handleOpenNotification = React.useCallback(
    (n: Notification) => {
      const page = pages.find((p) => p.id === n.parent_node_id);
      if (page) selectPage(page.id);
    },
    [pages, selectPage],
  );

  /**
   * F 333 — THE GEAR. A feature you can only reach by typing `?settings` is not
   * a product feature.
   *
   * WHY A FULL NAVIGATION AND NOT pushState: App.tsx reads `showSettings` from
   * `window.location.search` AT RENDER and never subscribes to history, so a
   * pushState would not re-render and the click would look broken. Assigning the
   * URL reloads, App re-evaluates, and the surface opens.
   *
   * WHY THIS CANNOT BREAK ROUTING: it never calls `selectPage()`, so
   * ActiveWorkspace's page-type dispatch — the thing F 320 fixed — is not on the
   * path at all. The current pathname is preserved, so it stays correct under
   * any sub-path the app is served from.
   */
  const openSettings = () => {
    const url = new URL(window.location.href);
    url.search = '?settings';
    url.hash = '';
    window.location.assign(url.toString());
  };

  // Pre-compute the list of available themes for the picker
  const availableThemes = (Object.values(allThemes) as Array<typeof theme>).map(s => ({
    id: s.id,
    label: s.name,
    description: s.description,
    swatch: `linear-gradient(135deg, ${s.primaryGradient[0]} 0%, ${s.primaryGradient[1]} 100%)`,
  }));

  /**
   * F 443 — SEARCH THE REAL GRAPH, NOT THE FAKE LIST.
   *
   * THE BUG: this bar filtered only `pages`, and `pages` is whatever
   * `WorkspaceContext` holds. When storage is empty that context seeds THREE
   * hardcoded DEFAULT_PAGES and never reconciles them against
   * `freshcards:databases`. The operator typed "quick notes" — a real database
   * with 641 items — and got nothing: no hit, and no empty state either.
   *
   * THE FIX: search the UNION of the two real sources.
   *   - `loadDatabases()` — `freshcards:databases`, the real graph (F 144
   *     RemoteState, already the pattern AIChatPanel and
   *     inlineDatabaseSuggestion use: a synchronous read of the same store).
   *   - `pages`          — `freshcards:pages`, kept so the dispatch's BOUNDS
   *     hold and existing page search does not regress.
   *
   * THREE THINGS THIS DELIBERATELY DOES NOT DO:
   *   1. It does not REPLACE the pages list. Only what the search reads changed.
   *   2. It never throws. A graph failure degrades to searching `pages`, so a
   *      down graph cannot make the header blank.
   *   3. It is not a gate. No result set is never rendered as an empty box —
   *      the empty state below always says so in words.
   *
   * De-duplicated BY ID because the live graph carries "Quick Notes" in both
   * keys, and a record that appears twice reads as two databases.
   *
   * WHY THIS IS NOT A useMemo — measured on live, and it is the whole bug.
   * The first version of this fix memoised the index on `[pages]` and it
   * searched nothing in production even though `freshcards:databases` was in
   * localStorage with 338,694 bytes of real data. `pages` does not change
   * when the graph hydrates, so the memo was computed ONCE at mount — before
   * `RemoteState.init()` finished — and never recomputed. The bar then filtered
   * an empty store forever.
   *
   * So the index is rebuilt on every keystroke instead. That is affordable:
   * `loadDatabases()` is a synchronous in-memory `Map` read (F 144), and the
   * live graph is 7 databases / 12 pages — re-walking a few hundred short
   * strings per character typed is not a cost worth a stale memo. Correctness
   * beats a micro-optimisation here, and a memo that can go stale against an
   * asynchronously-hydrated store is a trap, not an optimisation.
   */
  const buildSearchIndex = React.useCallback((): Array<{ id: string; title: string; kind: 'database' | 'page' }> => {
    const rows: Array<{ id: string; title: string; kind: 'database' | 'page' }> = [];
    const seen = new Set<string>();

    const push = (id: string, title: string, kind: 'database' | 'page') => {
      if (!id || seen.has(id)) return;
      // A record with no usable title is still reachable by its id, so it is
      // searchable rather than dropped — but it must never make the filter
      // call `.toLowerCase()` on undefined.
      const label = typeof title === 'string' && title.trim().length > 0
        ? title.trim()
        : id;
      seen.add(id);
      rows.push({ id, title: label, kind });
    };

    try {
      const dbs = loadDatabases();
      for (const [id, db] of Object.entries(dbs || {})) {
        push(id, (db as { name?: unknown }).name as string, 'database');
      }
    } catch (error) {
      // Fail open to the pages list. A graph outage must not empty the header.
      console.warn('[WorkspaceHeader] graph unavailable, searching pages only:', error);
    }

    for (const page of pages || []) {
      push(page?.id, page?.title, 'page');
    }
    return rows;
  }, [pages]);

  /**
   * Match on any whitespace-separated word, like VSCode's quick search.
   * Recomputed per keystroke for the reason above.
   */
  const searchResults = (() => {
    const terms = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];
    return buildSearchIndex()
      .filter((row) => {
        const hay = `${row.title} ${row.id}`.toLowerCase();
        return terms.every((t) => hay.includes(t));
      })
      .slice(0, 8);
  })();

  // Use CSS variables from the FreshCards theme context instead of
  // hardcoded values. This way dark/light mode switches via the theme
  // provider, and there is no race with Mantine's color scheme hook.
  const bg = 'var(--fc-bg-primary, #ffffff)';
  const border = 'var(--fc-border-primary, #e5e5e5)';
  const text = 'var(--fc-text-primary, #1a1a1a)';
  const inputBg = 'var(--fc-bg-secondary, #fafafa)';

  return (
    <Box className="fc-header-root" style={{ height: 'var(--fc-header-height, 50px)', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', padding: '0 16px', backgroundColor: bg, color: text, gap: 12, position: 'sticky', top: 0, zIndex: 1100, flexWrap: 'nowrap', overflow: 'visible' }}>
      {/* Left — toggle + brand */}
      <Group gap="xs" style={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden' }}>
        <ActionIcon variant="subtle" size="md" title="Menu" onClick={toggleSidebar}><IconMenu2 size={18} /></ActionIcon>
        <Text fw={600} size="sm" style={{ letterSpacing: '-0.01em', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }} onClick={goToHome}>
          <span className="fc-header-brand-full">FreshCards</span>
          <span className="fc-header-brand-short">{currentPage?.title || 'FreshCards'}</span>
        </Text>
        <ActionIcon className="fc-header-hide-mobile" variant="subtle" size="sm" title="Workspace options" onClick={() => {}}><IconChevronDown size={14} /></ActionIcon>
      </Group>

      {/* Center — search (hidden on mobile) */}
      <Box className="fc-header-search" style={{ flex: '1 1 0', minWidth: 0, maxWidth: 600, margin: '0 auto', position: 'relative' }}>
        <TextInput
          placeholder="Search pages..."
          size="sm"
          leftSection={<IconSearch size={16} stroke={1.5} />}
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setShowResults(e.target.value.length > 0); }}
          styles={{ root: { width: '100%' }, input: { borderRadius: 4, backgroundColor: inputBg, border: searchFocused ? '1px solid #1a73e8' : '1px solid transparent' } }}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
        />
        {showResults && searchQuery && (
          /* F 443 — THE PANEL HAS TO BE PAINTED ABOVE THE PAGE.
           *
           * Measured on live: the result row rendered at y=47 inside the header,
           * and `document.elementFromPoint()` at its centre returned `MAIN`,
           * not the row. So the search FOUND "Quick Notes" and the row was
           * still under the sidebar — invisible and unclickable. A search that
           * finds the right row and cannot be clicked is not a working search.
           *
           * Two causes, both fixed here:
           *   1. The panel sat at z-index 1000 INSIDE a parent that is already a
           *      stacking context at 1100, so 1000 could never lift it above the
           *      page. It is now 1200.
           *   2. `.fc-header-root` sets `overflow: hidden` to stop its flex row
           *      overflowing on narrow viewports — and that also CLIPS an
           *      absolutely-positioned child. `visible` on the root keeps the
           *      layout guard on the row itself (`.fc-header-brand-*` ellipsis)
           *      while letting the panel escape.
           */
          <Box style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: bg, borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 300, overflow: 'auto', zIndex: 1200, marginTop: 4 }}>
            {/* F 443 — an empty result set says so. The old bar rendered an
                empty box here, which is indistinguishable from "broken". */}
            {searchResults.length === 0 ? (
              <Box p="sm">
                <Text size="sm" c="dimmed">No results found</Text>
              </Box>
            ) : (
              searchResults.map((row) => (
                <Box key={`${row.kind}:${row.id}`} p="sm" style={{ cursor: 'pointer', borderBottom: '1px solid #e9e9e7' }}
                  data-testid="fc-header-search-result"
                  onClick={() => { setSearchQuery(''); setShowResults(false); selectPage(row.id); }}>
                  <Group gap="xs" justify="space-between" wrap="nowrap">
                    <Text size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.title}</Text>
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{row.kind === 'database' ? 'database' : 'page'}</Text>
                  </Group>
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>

      {/* Right — actions */}
      <Group gap={4} style={{ flex: '0 0 auto' }}>
        {/* F 423 — THE BUILD NUMBER.
         *
         * The operator asked for the build number in the top bar. It is the
         * git short hash the app is BUILT from — the same value
         * `/version.json` serves as `buildHash` and the same one every report
         * in this project names. It is here so that "which build am I
         * looking at?" is answerable by LOOKING, not by opening a URL and
         * trusting a proxy.
         *
         * It sits immediately before the `?` icon, in small muted text, and
         * it carries the full version in its tooltip. It hides together with
         * the `?` (`fc-header-hide-narrow`), because a build number with no
         * question mark next to it is a stray label, not a header.
         */}
        <Tooltip label={`FreshCards ${freshcardsVersion} · build ${freshcardsBuildHash}`} openDelay={400}>
          <Text
            className="fc-header-hide-narrow fc-header-build"
            size="xs"
            c="dimmed"
            data-testid="fc-header-build"
            style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em', whiteSpace: 'nowrap', lineHeight: 1 }}
          >
            {freshcardsBuildHash}
          </Text>
        </Tooltip>
        <ActionIcon className="fc-header-icon fc-header-hide-narrow" variant="subtle" size="md" title="Help" onClick={() => alert('FreshCards v1.0\nKeyboard: Ctrl+N, Ctrl+S, Ctrl+/')}><IconQuestionMark size={18} /></ActionIcon>
        {/* F 490 — the bell became a real notification centre. It used to be
            `onClick={() => alert('No notifications')}`: the bell's SHAPE was
            already proven in production, but it had no feed, no badge and no
            inbox. It keeps its exact position so the header does not move. */}
        <NotificationCentre store={reminderStore} onOpenNotification={handleOpenNotification} />
        <Divider className="fc-header-hide-narrow" orientation="vertical" style={{ height: 24 }} />
        <Tooltip label={quickNotePin.isPinned ? 'Pinned — always open' : 'Quick Note'} openDelay={400}>
          <ActionIcon className="fc-header-icon" variant={isPeekOpen('quick-note') ? 'filled' : 'subtle'} style={{ color: isPeekOpen('quick-note') ? 'var(--fc-accent-primary)' : 'var(--fc-text-hint)' }} onClick={() => handleTogglePeek('quick-note')} size="md" title="Quick Note"><IconPlus size={18} /></ActionIcon>
        </Tooltip>
        <Tooltip label={aiChatPin.isPinned ? 'Pinned — always open' : 'AI Chat'} openDelay={400}>
          <ActionIcon className="fc-header-icon" variant={isPeekOpen('ai-chat') ? 'filled' : 'subtle'} color="grape" onClick={() => handleTogglePeek('ai-chat')} size="md" title="AI Chat"><IconSparkles size={18} /></ActionIcon>
        </Tooltip>
        <ActionIcon className="fc-header-icon" variant="subtle" onClick={() => { toggleColorScheme(); setMode(modeName === 'dark' ? 'light' : 'dark'); }} size="md" title={modeName === 'dark' ? 'Light mode' : 'Dark mode'}>
          {modeName === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
        </ActionIcon>
        <Menu className="fc-header-hide-narrow" position="bottom-end" shadow="md" width={240}>
          <Menu.Target>
            <Tooltip label="Theme: switch product palette">
              <ActionIcon className="fc-header-icon" variant="subtle" size="md" title="Theme">
                <IconPalette size={18} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Active theme</Menu.Label>
            {availableThemes.map(s => (
              <Menu.Item
                key={s.id}
                onClick={() => setTheme(s.id as ThemeId)}
                leftSection={
                  <Box
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 4,
                      background: s.swatch,
                      border: '1px solid var(--fc-border-secondary, #d4d4d1)',
                      flexShrink: 0,
                    }}
                  />
                }
                rightSection={themeId === s.id ? <Text size="xs" c="dimmed">✓</Text> : null}
              >
                <Text size="sm" fw={500}>{s.label}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
            <Menu.Label>Overlays live in app-overlays/</Menu.Label>
          </Menu.Dropdown>
        </Menu>
                  {/* F 333 — the gear. Placed beside the other top-bar affordances so
              the AI Engine is one click away, not one remembered URL. */}
          <Tooltip label="Settings" openDelay={400}>
            <ActionIcon className="fc-header-icon" variant="subtle" size="md" title="Settings"
              data-testid="fc-header-settings" onClick={openSettings}>
              <IconSettings size={18} />
            </ActionIcon>
          </Tooltip>
          <Menu className="fc-header-hide-narrow" position="bottom-end" shadow="md" width={200}>
          <Menu.Target>
            <ActionIcon className="fc-header-icon" variant="subtle" size="md" radius="xl"><Avatar size={28} color="blue" radius="xl">U</Avatar></ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Signed in as User</Menu.Label>
            <Menu.Item leftSection={<IconUser size={14} />} onClick={() => setProfileOpen(true)} data-testid="fc-header-profile">Profile</Menu.Item>
            <Menu.Item leftSection={<IconSettings size={14} />} onClick={openSettings}>Settings</Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" leftSection={<IconLogout size={14} />} onClick={() => alert('Signed out!')}>Sign out</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
      {/* F 345 — the Profile Face. Mounted here so the avatar Menu in the
          top bar is the only way to reach it; the modal renders nothing
          when closed, so the top bar's render cost is unchanged. */}
      <Profile opened={profileOpen} onClose={() => setProfileOpen(false)} />
    </Box>
  );
};